import React, { useCallback, useEffect, useState } from 'react';
import { ShoppingCartIcon, TrendingUpIcon, PackageIcon, BarChart3Icon, FileTextIcon, RefreshCwIcon, ChevronDownIcon, ChevronUpIcon, UserIcon } from 'lucide-react';
import { Card, CardHeader } from '../components/ui/Card';
import { StatsCard } from '../components/ui/StatsCard';
import { Badge } from '../components/ui/Badge';
import { Button } from '../components/ui/Button';
import { useAuth } from '../context/AuthContext';
import { useApp } from '../context/AppContext';
import { Pagination } from '../components/ui/Pagination';
import { PageHeader } from '../components/ui/PageHeader';
import { supabaseAdmin } from '../lib/supabase';
import { useRealtimeRefresh } from '../hooks/useRealtimeRefresh';
interface SaleItem {
  id: string;
  clienteNombre: string;
  vendedorId: string;
  vendedorNombre: string;
  fecha: Date;
  zona: string;
  montoTotal: number;
  subTotal: number;
  itbis: number;
  referencia: string;
  comentarios: string;
  source: 'visita' | 'factura';
}
export function SalesPage() {
  const {
    user
  } = useAuth();
  const {
    users,
    totalVendido: totalVendidoGlobal,
    isLoading: appLoading
  } = useApp();
  const isAdmin = user?.rolNombre === 'Administrador' || user?.permisos?.users === true;
  const canSeeAll = isAdmin || user?.permisos?.cuentas_cobrar === true;
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [vendorFilter, setVendorFilter] = useState('all');
  const [showVendorStats, setShowVendorStats] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const ITEMS_PER_PAGE = 8;

  // Only actual vendedores
  const vendedorIds = new Set(users.filter(u => u.activo && u.permisos?.['new-visit'] === true).map(u => u.id));
  const vendedorNames = new Set(users.filter(u => u.activo && u.permisos?.['new-visit'] === true).map(u => u.nombre));

  // Load invoices from Supabase
  const [invoices, setInvoices] = useState<any[]>([]);
  const loadInvoices = useCallback(async () => {
    try {
      const {
        data,
        error
      } = await supabaseAdmin.from('invoices').select('id,numero_factura,farmacia,vendedor,vendedor_id,fecha_emision,sub_total,itbis,monto,estado').not('estado', 'eq', 'anulada').gte('fecha_emision', new Date(Date.now() - 90 * 864e5).toISOString().split('T')[0]).order('fecha_emision', {
        ascending: false
      }).limit(300);
      if (data) setInvoices(data);
      if (error) console.error('Error loading invoices for sales:', error);
    } catch {}
  }, []);
  useEffect(() => {
    loadInvoices();
  }, [loadInvoices]);
  useRealtimeRefresh(['invoices'], loadInvoices);
  const vendedorIdToName: Record<string, string> = {};
  users.filter(u => u.activo && u.permisos?.['new-visit'] === true).forEach(u => {
    vendedorIdToName[u.id] = u.nombre;
  });
  const vendedorNameNormalized: Record<string, string> = {};
  users.filter(u => u.activo && u.permisos?.['new-visit'] === true).forEach(u => {
    vendedorNameNormalized[u.nombre.trim().toLowerCase()] = u.nombre;
  });
  const allSales = invoices.map(inv => ({
    id: `f-${inv.id}`,
    clienteNombre: inv.farmacia,
    vendedorId: inv.vendedor_id || '',
    vendedorNombre: inv.vendedor || '',
    fecha: new Date(inv.fecha_emision),
    zona: '',
    montoTotal: inv.monto || 0,
    subTotal: inv.sub_total || 0,
    itbis: inv.itbis || 0,
    referencia: inv.numero_factura || '',
    comentarios: '',
    source: 'factura' as const
  }) as SaleItem).filter(s => canSeeAll || s.vendedorId === user?.id).filter(s => {
    const saleDate = s.fecha.toLocaleDateString('en-CA', {
      timeZone: 'America/Santo_Domingo'
    });
    if (dateFrom && saleDate < dateFrom) return false;
    if (dateTo && saleDate > dateTo) return false;
    return true;
  }).filter(s => {
    if (vendorFilter === 'all') return true;
    return s.vendedorId === vendorFilter || s.vendedorNombre === vendorFilter;
  }).sort((a, b) => b.fecha.getTime() - a.fecha.getTime());
  const totalVendido = allSales.reduce((sum, s) => sum + s.montoTotal, 0);
  const totalPedidos = allSales.length;
  const promedioVenta = totalPedidos > 0 ? totalVendido / totalPedidos : 0;
  const today = new Date();
  const todaySales = allSales.filter(s => s.fecha.toDateString() === today.toDateString());
  const todayTotal = todaySales.reduce((sum, s) => sum + s.montoTotal, 0);
  const formatCurrency = (amount: number) => new Intl.NumberFormat('es-DO', {
    style: 'currency',
    currency: 'DOP',
    minimumFractionDigits: 0
  }).format(amount);
  const formatDate = (date: Date) => date.toLocaleDateString('es-DO', {
    weekday: 'short',
    day: '2-digit',
    month: 'short',
    year: 'numeric'
  });
  const totalPages = Math.ceil(allSales.length / ITEMS_PER_PAGE);
  const paginatedSales = allSales.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);
  const salesByVendor = isAdmin ? allSales.filter(s => vendedorIds.has(s.vendedorId) || vendedorNames.has(s.vendedorNombre) || vendedorNameNormalized[s.vendedorNombre.trim().toLowerCase()]).reduce((acc, s) => {
    const name = vendedorIdToName[s.vendedorId] || vendedorNameNormalized[s.vendedorNombre.trim().toLowerCase()] || s.vendedorNombre || 'Sin vendedor';
    if (!acc[name]) acc[name] = {
      total: 0,
      count: 0
    };
    acc[name].total += s.montoTotal;
    acc[name].count += 1;
    return acc;
  }, {} as Record<string, {
    total: number;
    count: number;
  }>) : {};
  const activeVendors = users.filter(u => u.activo && u.rolNombre !== 'Administrador' && u.permisos?.['new-visit'] === true);
  const maxVendorTotal = Math.max(...Object.values(salesByVendor).map(d => d.total), 1);

  // Avatar initials color palette
  const avatarColors = ['bg-blue-100 text-blue-700', 'bg-emerald-100 text-emerald-700', 'bg-violet-100 text-violet-700', 'bg-amber-100 text-amber-700', 'bg-rose-100 text-rose-700', 'bg-cyan-100 text-cyan-700'];
  const getAvatarColor = (name: string) => avatarColors[name.charCodeAt(0) % avatarColors.length];
  return <div className="space-y-4 sm:space-y-6" data-id="element-4410" data-mp="9krx7wm8sjn7">
      <PageHeader title="Registro de Ventas" subtitle="Historial de ventas y facturación" icon={<ShoppingCartIcon className="w-6 h-6" data-id="element-4412" data-mp="2ud3iu720igx2" />} data-id="element-4411" data-mp="2qb08jlr2xidd">
        <div className="flex items-center gap-2 w-full sm:w-auto flex-wrap" data-id="element-4413" data-mp="2uahba7f4g1eu">
          <div className="flex items-center gap-1.5" data-id="element-4414" data-mp="2mrhtg67b6hxl">
            <span className="text-xs text-slate-500 font-medium" data-id="element-4415" data-mp="2hu85kuzkiebv">Desde</span>
            <input type="date" value={dateFrom} onChange={e => {
            setDateFrom(e.target.value);
            setCurrentPage(1);
          }} className="rounded-lg border border-gray-300 bg-white px-2.5 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-4416" data-mp="2hwc7x8htj5a1" />
          </div>
          <div className="flex items-center gap-1.5" data-id="element-4417" data-mp="1z408atijui3l">
            <span className="text-xs text-slate-500 font-medium" data-id="element-4418" data-mp="1z1dqm11y2m7n">Hasta</span>
            <input type="date" value={dateTo} onChange={e => {
            setDateTo(e.target.value);
            setCurrentPage(1);
          }} className="rounded-lg border border-gray-300 bg-white px-2.5 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-4419" data-mp="2dt63m6e9no67" />
          </div>
          <Button variant="secondary" onClick={() => loadInvoices()} title="Recargar" data-id="element-4420" data-mp="1gawbx0dy5r5a">
            <RefreshCwIcon className="w-4 h-4" data-id="element-4421" data-mp="uvibrgekjkv4" />
          </Button>
          {(dateFrom || dateTo || vendorFilter !== 'all') && <Button variant="secondary" onClick={() => {
          setDateFrom('');
          setDateTo('');
          setVendorFilter('all');
        }} data-id="element-4422" data-mp="14rv8yo9bogum">
              Limpiar
            </Button>}
        </div>
      </PageHeader>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 animate-stagger-1" data-id="element-4423" data-mp="1ds9thlegprx2">
        <StatsCard title="Total Vendido" value={formatCurrency(totalVendido)} icon={<ShoppingCartIcon className="w-5 h-5" data-id="element-4425" data-mp="1nj4slqct4lzx" />} variant="primary" data-id="element-4424" data-mp="1iq1xvewan6i5" />
        <StatsCard title="Ventas Hoy" value={formatCurrency(todayTotal)} subtitle={`${todaySales.length} registros`} icon={<TrendingUpIcon className="w-5 h-5" data-id="element-4427" data-mp="21huo51jzadm6" />} variant="success" data-id="element-4426" data-mp="1sg4q3cvlcgff" />
        <StatsCard title="Total Registros" value={totalPedidos} icon={<PackageIcon className="w-5 h-5" data-id="element-4429" data-mp="3lxshjuth4p09" />} variant="warning" data-id="element-4428" data-mp="3m057ov9dy5oe" />
        <StatsCard title="Promedio por Venta" value={formatCurrency(promedioVenta)} icon={<BarChart3Icon className="w-5 h-5" data-id="element-4431" data-mp="3d1kxkihw54bs" />} variant="default" data-id="element-4430" data-mp="3gzqyh9w1spos" />
      </div>

      {/* Vendor filter + list — full width vertical */}
      <Card padding="none" className="animate-stagger-2" data-id="element-4432" data-mp="8k7aapa8hxgl">
        {/* Header */}
        <div className="px-4 sm:px-6 py-4 border-b border-slate-100 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3" data-id="element-4433" data-mp="4j59w54kzfd1">
          <div data-id="element-4434" data-mp="3vwrsjk1powqf">
            <h2 className="text-base font-bold text-slate-900" data-id="element-4435" data-mp="3vw873tcurq7n">Historial de Ventas</h2>
            <p className="text-xs text-slate-500 mt-0.5" data-id="element-4436" data-mp="3qyzpbtvm9o6x">{allSales.length} ventas registradas</p>
          </div>
          {/* Vendor filter (admin only) */}
          {isAdmin && activeVendors.length > 0 && <div className="flex items-center gap-2 flex-wrap" data-id="element-4437" data-mp="ngg77k6njypu">
              <button onClick={() => {
            setVendorFilter('all');
            setCurrentPage(1);
          }} className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${vendorFilter === 'all' ? 'bg-[#2952C4] text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`} data-id="element-4438" data-mp="31f195og8r7pi">
                Todos
              </button>
              {activeVendors.map(v => <button key={v.id} onClick={() => {
            setVendorFilter(v.id);
            setCurrentPage(1);
          }} className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${vendorFilter === v.id ? 'bg-[#2952C4] text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`} data-id="element-4439" data-mp="3fesu6956dod0">
                  {v.nombre.split(' ')[0]}
                </button>)}
            </div>}
        </div>

        {/* List */}
        {appLoading && allSales.length === 0 ? <div className="p-16 text-center" data-id="element-4440" data-mp="qjhhejmu99zz">
            <div className="w-8 h-8 border-3 border-[#2952C4] border-t-transparent rounded-full animate-spin mx-auto mb-4" data-id="element-4441" data-mp="cki3a5wycdi2" />
            <p className="text-slate-500 text-sm" data-id="element-4442" data-mp="gl1zn79pbd35">Cargando ventas...</p>
          </div> : allSales.length === 0 ? <div className="p-16 text-center" data-id="element-4443" data-mp="2su75rpga8a9">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-slate-100 rounded-2xl mb-4" data-id="element-4444" data-mp="7kvdyy3un6sw">
              <ShoppingCartIcon className="w-8 h-8 text-slate-400" data-id="element-4445" data-mp="3p255nmwtkof8" />
            </div>
            <h3 className="text-lg font-semibold text-slate-900 mb-1" data-id="element-4446" data-mp="3u0py305ty3t4">No hay ventas</h3>
            <p className="text-slate-500 text-sm" data-id="element-4447" data-mp="3tydj8vvdc9ka">
              No hay ventas registradas en el período seleccionado
            </p>
          </div> : <>
            <div className="divide-y divide-slate-100" data-id="element-4448" data-mp="3kz8gfdcrxu2b">
              {paginatedSales.map(sale => {
            const initials = sale.clienteNombre.split(' ').slice(0, 2).map(w => w[0]).join('').toUpperCase();
            const avatarColor = getAvatarColor(sale.clienteNombre);
            const hasItbis = sale.itbis > 0;
            return <div key={sale.id} className="px-4 sm:px-6 py-4 hover:bg-slate-50/60 transition-colors" data-id="element-4449" data-mp="29hmdmm45aggo">
                    <div className="flex items-start gap-3 sm:gap-4" data-id="element-4450" data-mp="20hqyk67gy1bd">
                      {/* Avatar */}
                      <div className={`flex-shrink-0 w-10 h-10 sm:w-11 sm:h-11 rounded-xl flex items-center justify-center text-sm font-bold ${avatarColor}`} data-id="element-4451" data-mp="1vjfobud9hosm">
                        {initials}
                      </div>

                      {/* Main content — vertical */}
                      <div className="flex-1 min-w-0" data-id="element-4452" data-mp="2vs5lu92cui83">
                        {/* Row 1: cliente + monto */}
                        <div className="flex items-start justify-between gap-2" data-id="element-4453" data-mp="30qz38ckritdj">
                          <h4 className="font-semibold text-slate-900 text-sm sm:text-base leading-tight" data-id="element-4454" data-mp="34qq08y0vu5qi">
                            {sale.clienteNombre}
                          </h4>
                          <p className="flex-shrink-0 text-base sm:text-lg font-bold text-[#2952C4]" data-id="element-4455" data-mp="3ei3x0y1g8rfy">
                            {formatCurrency(sale.montoTotal)}
                          </p>
                        </div>

                        {/* Row 2: referencia + vendedor + fecha */}
                        <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1" data-id="element-4456" data-mp="3shvg3d6toy0t">
                          {sale.referencia && <span className="inline-flex items-center gap-1 text-xs font-semibold text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-2 py-0.5" data-id="element-4457" data-mp="1u3bnuttqewwf">
                              <FileTextIcon className="w-3 h-3" data-id="element-4458" data-mp="12s4g4owijpsu" />
                              {sale.referencia}
                            </span>}
                          {(isAdmin || canSeeAll) && sale.vendedorNombre && <span className="inline-flex items-center gap-1 text-xs text-slate-500" data-id="element-4459" data-mp="1cozacvlr741b">
                              <UserIcon className="w-3 h-3" data-id="element-4460" data-mp="k2o2czvti0uh" />
                              {sale.vendedorNombre}
                            </span>}
                          <span className="text-xs text-slate-400" data-id="element-4461" data-mp="f5ecxk8fhp6n">
                            {formatDate(sale.fecha)}
                          </span>
                        </div>

                        {/* Row 3: subtotal + itbis breakdown */}
                        <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-0.5" data-id="element-4462" data-mp="1bn7oo1qcrgq">
                          <span className="text-xs text-slate-500" data-id="element-4463" data-mp="3sizp946iz6x6">
                            Sub Total:{' '}
                            <span className="font-semibold text-slate-700" data-id="element-4464" data-mp="280737w3v44el">
                              {formatCurrency(sale.subTotal)}
                            </span>
                          </span>
                          {hasItbis && <span className="text-xs text-slate-500" data-id="element-4465" data-mp="1yxo1jfbtbxtx">
                              ITBIS:{' '}
                              <span className="font-semibold text-slate-600" data-id="element-4466" data-mp="2qqx45f9vhh4t">
                                {formatCurrency(sale.itbis)}
                              </span>
                            </span>}
                        </div>

                        {/* Comments */}
                        {sale.comentarios && <p className="mt-1.5 text-xs text-slate-500 italic" data-id="element-4467" data-mp="2mo0j7hwv18xp">
                            {sale.comentarios}
                          </p>}
                      </div>
                    </div>
                  </div>;
          })}
            </div>

            {/* Footer total */}
            <div className="px-4 sm:px-6 py-3 bg-slate-50 border-t border-slate-100 flex items-center justify-between" data-id="element-4468" data-mp="38093t0y48foi">
              <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide" data-id="element-4469" data-mp="3390t81l1uo9r">
                Total período
              </span>
              <span className="text-base font-bold text-[#2952C4]" data-id="element-4470" data-mp="2z7fiomvzmm2q">
                {formatCurrency(totalVendido)}
              </span>
            </div>

            <div className="p-4 border-t border-slate-100" data-id="element-4471" data-mp="2p988cmw7f29p">
              <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} totalItems={allSales.length} itemsPerPage={ITEMS_PER_PAGE} data-id="element-4472" data-mp="2pbufwmj3hhrx" />
            </div>
          </>}
      </Card>

      {/* Ventas por Vendedor — collapsible, full width */}
      {isAdmin && Object.keys(salesByVendor).length > 0 && <Card padding="none" className="animate-stagger-3" data-id="element-4473" data-mp="1wfuxtojf0qn5">
          <button onClick={() => setShowVendorStats(v => !v)} className="w-full px-4 sm:px-6 py-4 flex items-center justify-between hover:bg-slate-50 transition-colors" data-id="element-4474" data-mp="1wd8rttc6aj71">
            <div className="text-left" data-id="element-4475" data-mp="267ijf8tajdgp">
              <h2 className="text-base font-bold text-slate-900" data-id="element-4476" data-mp="2a6zqu7sa2koi">Ventas por Vendedor</h2>
              <p className="text-xs text-slate-500 mt-0.5" data-id="element-4477" data-mp="1dh0gc7skhc3a">
                Basado en {invoices.length} facturas
              </p>
            </div>
            {showVendorStats ? <ChevronUpIcon className="w-4 h-4 text-slate-400" data-id="element-4478" data-mp="1mh5gpxzcfa7y" /> : <ChevronDownIcon className="w-4 h-4 text-slate-400" data-id="element-4479" data-mp="1mji5ov3iqkui" />}
          </button>

          {showVendorStats && <div className="px-4 sm:px-6 pb-4 border-t border-slate-100" data-id="element-4480" data-mp="2xwu42qgieb9a">
              <div className="pt-4 space-y-3" data-id="element-4481" data-mp="32vf0sfwjycsz">
                {Object.entries(salesByVendor).sort((a, b) => b[1].total - a[1].total).map(([vendedor, data], idx) => {
            const pct = Math.round(data.total / maxVendorTotal * 100);
            const color = getAvatarColor(vendedor);
            return <div key={vendedor} className="flex items-center gap-3 sm:gap-4" data-id="element-4482" data-mp="2nd9nuk664ol0" data-mpi={idx}>
                        {/* Rank */}
                        <span className="flex-shrink-0 w-5 text-xs font-bold text-slate-400 text-right" data-id="element-4483" data-mp="2x92y1wwf05zm">
                          #{idx + 1}
                        </span>
                        {/* Avatar */}
                        <div className={`flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center text-xs font-bold ${color}`} data-id="element-4484" data-mp="36aj6w07l001q">
                          {vendedor.charAt(0).toUpperCase()}
                        </div>
                        {/* Info + bar */}
                        <div className="flex-1 min-w-0" data-id="element-4485" data-mp="17vq0cqit5g7h">
                          <div className="flex items-center justify-between mb-1" data-id="element-4486" data-mp="xqgeiye71dml">
                            <span className="text-sm font-semibold text-slate-800 truncate" data-id="element-4487" data-mp="xsu5ow5r65ov">
                              {vendedor}
                            </span>
                            <div className="flex-shrink-0 flex items-center gap-2 ml-2" data-id="element-4488" data-mp="o055ixfnke8u">
                              <span className="text-xs text-slate-400" data-id="element-4489" data-mp="sygfr99v0qrl">
                                {data.count} vtas
                              </span>
                              <span className="text-sm font-bold text-[#2952C4]" data-id="element-4490" data-mp="j1btzajxexbb">
                                {formatCurrency(data.total)}
                              </span>
                            </div>
                          </div>
                          <div className="w-full bg-slate-100 rounded-full h-1.5" data-id="element-4491" data-mp="a1f9zm8hemml">
                            <div className="bg-[#2952C4] h-1.5 rounded-full transition-all duration-500" style={{
                    width: `${pct}%`
                  }} data-id="element-4492" data-mp="1zgv886kvanxp" />
                          </div>
                        </div>
                      </div>;
          })}
              </div>
            </div>}
        </Card>}
    </div>;
}